import React, { useId, useState } from "react";

import { useTranslation } from "lib/i18n/use-translation";

import styles from "./info.module.scss";

// Opens on hover, on keyboard focus, and on click or tap. The click is
// tracked in state rather than left to :focus, because Safari does not focus
// a button it clicks, so a focus-only tooltip never opens there on a tap.
// Escape dismisses it until focus leaves, hover and keyboard focus included.
type InfoMode = "closed" | "open" | "dismissed";

const Info = ({
  figure,
  text,
  align = "end",
}: {
  figure: string;
  text: string;
  // Which edge of the button the tooltip lines up with.
  align?: "start" | "end";
}) => {
  const { t } = useTranslation(["admin"]);
  const id = useId();
  const [mode, setMode] = useState<InfoMode>("closed");
  return (
    <span className={styles["info"]} data-mode={mode} data-align={align}>
      <button
        type="button"
        className={styles["info-button"]}
        aria-label={t("admin:companyDashboard.aboutFigure", { figure })}
        aria-expanded={mode === "open"}
        onClick={() => setMode((was) => (was === "open" ? "closed" : "open"))}
        onBlur={() => setMode("closed")}
        onKeyDown={(event) => {
          if (event.key === "Escape") setMode("dismissed");
        }}
        aria-describedby={id}
      >
        <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <circle cx="10" cy="10" r="8.25" fill="none" stroke="currentColor" />
          <path
            d="M10 9v5M10 6.2v.1"
            stroke="currentColor"
            strokeLinecap="round"
          />
        </svg>
      </button>
      <span role="tooltip" id={id} className={styles["info-text"]}>
        {text}
      </span>
    </span>
  );
};

export default Info;
